// ─────────────────────────────────────────────
// KLOTHIQ — usePlans hook
// ─────────────────────────────────────────────
// Queries and mutations for plans management.
// ─────────────────────────────────────────────

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import {
  normalizeWorkTask,
  workBusinessToday,
  workCompletionPercent,
  type WorkTask,
} from '@klothiq/domain';
import { workRpc, workErrorMessage } from '@/lib/work-planning';

export const PLANS_KEY = 'plans';
export const PLAN_DETAILS_KEY = 'plan_details';

export interface PlanWithMetrics {
  version: number;
  id: string;
  organization_id: string;
  title: string;
  description: string | null;
  goal: string | null;
  start_date: string | null;
  target_date: string | null;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  is_archived: boolean;
  archived_at: string | null;
  archived_by: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  total_tasks: number;
  completed_tasks: number;
  active_tasks: number;
  blocked_tasks: number;
  progress_pct: number;
  is_overdue: boolean;
}

export interface PlanDetailsResponse {
  plan: {
    version: number;
    updated_at: string;
    id: string;
    title: string;
    description: string | null;
    goal: string | null;
    start_date: string | null;
    target_date: string | null;
    priority: 'low' | 'normal' | 'high' | 'urgent';
    is_archived: boolean;
    created_at: string;
    created_by_name?: string | null;
  };
  metrics: {
    total_tasks: number;
    completed_tasks: number;
    in_progress_tasks: number;
    todo_tasks: number;
    blocked_tasks: number;
    cancelled_tasks: number;
    progress_percent: number;
  };
  tasks: WorkTask[];
}

export function usePlansList(options?: {
  archiveState?: 'active' | 'archived' | 'all';
  search?: string;
  priority?: string;
}) {
  const { organization, profile, access, can } = useAuth();
  const orgId = organization?.id;

  return useQuery({
    queryKey: [PLANS_KEY, orgId, profile?.id, access?.access_version, options],
    queryFn: async (): Promise<PlanWithMetrics[]> => {
      let query = supabase
        .from('plans')
        .select(
          `
          id,
          version,
          organization_id,
          title,
          description,
          goal,
          start_date,
          target_date,
          priority,
          is_archived,
          archived_at,
          archived_by,
          created_at,
          updated_at,
          created_by,
          plan_tasks (
            id,
            status,
            progress,
            due_date
          )
        `,
        )
        .eq('organization_id', orgId!)
        .order('created_at', { ascending: false });

      if (options?.archiveState !== 'all')
        query = query.eq('is_archived', options?.archiveState === 'archived');

      if (options?.priority && options.priority !== 'all') {
        query = query.eq('priority', options.priority);
      }

      const { data, error } = await query;
      if (error) throw new Error(workErrorMessage(error));

      const today = workBusinessToday();

      return (data || [])
        .map((p: any) => {
          const tasks: any[] = p.plan_tasks || [];
          const nonCancelled = tasks.filter((t) => t.status !== 'cancelled');
          const completed = nonCancelled.filter((t) => t.status === 'completed');
          const blocked = nonCancelled.filter((t) => t.status === 'blocked');
          const active = nonCancelled.filter((t) => t.status !== 'completed');

          const totalCount = nonCancelled.length;
          const completedCount = completed.length;
          const progressPct = workCompletionPercent(completedCount, totalCount);

          const isOverdue =
            !!p.target_date &&
            p.target_date < today &&
            completedCount < totalCount &&
            !p.is_archived;

          return {
            id: p.id,
            version: p.version,
            organization_id: p.organization_id,
            title: p.title,
            description: p.description,
            goal: p.goal,
            start_date: p.start_date,
            target_date: p.target_date,
            priority: p.priority,
            is_archived: p.is_archived,
            archived_at: p.archived_at,
            archived_by: p.archived_by,
            created_at: p.created_at,
            updated_at: p.updated_at,
            created_by: p.created_by,
            total_tasks: totalCount,
            completed_tasks: completedCount,
            active_tasks: active.length,
            blocked_tasks: blocked.length,
            progress_pct: progressPct,
            is_overdue: isOverdue,
          };
        })
        .filter((plan) => {
          if (!options?.search) return true;
          const s = options.search.toLowerCase();
          return (
            plan.title.toLowerCase().includes(s) ||
            (plan.goal && plan.goal.toLowerCase().includes(s)) ||
            (plan.description && plan.description.toLowerCase().includes(s))
          );
        });
    },
    enabled: !!orgId && can('plans.view'),
    staleTime: 1000 * 30, // 30s
  });
}

export function usePlanDetails(planId: string | undefined) {
  const { organization, access } = useAuth();
  return useQuery({
    queryKey: [PLAN_DETAILS_KEY, planId, organization?.id, access?.access_version],
    queryFn: async (): Promise<PlanDetailsResponse> => {
      const data = await workRpc<PlanDetailsResponse>('get_work_plan_v2', { p_plan_id: planId });
      return { ...data, tasks: data.tasks.map(normalizeWorkTask) };
    },
    enabled: !!planId && !!organization?.id,
    staleTime: 1000 * 15,
    refetchInterval: 30000,
  });
}

export { useCreatePlan, useUpdatePlan, useArchivePlan } from './useWorkMutations';
